import React, { useState, useEffect, createContext, useContext, ReactNode, useRef } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { 
  Calendar, MapPin, Clock, Users, Heart, 
  Mail, Send, Edit, Trash2, Plus, X, ChevronRight,
  Image, Bell, CheckCircle, LogOut,
  BarChart3, Upload, Sparkles,
  Minimize2, Bot, Loader, Eye,
  CreditCard, DollarSign, TrendingUp, Settings,
  FileText, Wand2, ImagePlus, Check
} from 'lucide-react';

// ============ Config ============
const OPENAI_API_KEY = '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';

// ============ Types ============
interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  phone?: string;
  neighborhood?: string;
  joinedAt?: string;
  eventsAttended?: number;
}

interface Event {
  id: string;
  title: string;
  description: string;
  content: string;
  date: string;
  time: string;
  location: string;
  price: number;
  capacity: number;
  registered: number;
  image: string;
  category: string;
}

interface Newsletter {
  id: string;
  title: string;
  content: string;
  sentAt?: string;
  createdAt: string;
  recipientCount?: number;
}

interface Announcement {
  id: string;
  message: string;
  active: boolean;
  createdAt: string;
}

interface GalleryImage {
  id: string;
  url: string;
  caption: string;
  eventId?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// ============ Context ============
interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// ============ AI Helper ============
const generateWithAI = async (prompt: string): Promise<string> => {
  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: 'You are a helpful assistant for Vivonne SF, a mom meetup group in San Francisco. Keep responses friendly, warm, and concise. For event descriptions, make them engaging and fun. For newsletters, be warm and community-focused.' },
          { role: 'user', content: prompt }
        ],
        max_tokens: 1000
      })
    });
    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error('AI Error:', error);
    return 'Unable to generate content. Please try again.';
  }
};

// ============ Image Upload Helper ============
const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

// ============ Data Context ============
interface DataContextType {
  events: Event[];
  setEvents: React.Dispatch<React.SetStateAction<Event[]>>;
  gallery: GalleryImage[];
  setGallery: React.Dispatch<React.SetStateAction<GalleryImage[]>>;
  newsletters: Newsletter[];
  setNewsletters: React.Dispatch<React.SetStateAction<Newsletter[]>>;
  announcements: Announcement[];
  setAnnouncements: React.Dispatch<React.SetStateAction<Announcement[]>>;
  members: User[];
  setMembers: React.Dispatch<React.SetStateAction<User[]>>;
}

const DataContext = createContext<DataContextType | null>(null);

const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within DataProvider');
  return context;
};

const DataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [events, setEvents] = useState<Event[]>(() => {
    const saved = localStorage.getItem('vivonne_events');
    return saved ? JSON.parse(saved) : [];
  });

  const [gallery, setGallery] = useState<GalleryImage[]>(() => {
    const saved = localStorage.getItem('vivonne_gallery');
    if (saved) {
      const parsed = JSON.parse(saved);
      // If empty, return default with supper club image
      if (parsed.length === 0) {
        return [{ id: '1', url: '/supper-club.jpg', caption: 'Supper Club' }];
      }
      return parsed;
    }
    // Default with the supper club image
    return [{ id: '1', url: '/supper-club.jpg', caption: 'Supper Club' }];
  });

  const [newsletters, setNewsletters] = useState<Newsletter[]>(() => {
    const saved = localStorage.getItem('vivonne_newsletters');
    return saved ? JSON.parse(saved) : [];
  });

  const [announcements, setAnnouncements] = useState<Announcement[]>(() => {
    const saved = localStorage.getItem('vivonne_announcements');
    return saved ? JSON.parse(saved) : [];
  });

  const [members, setMembers] = useState<User[]>(() => {
    const saved = localStorage.getItem('vivonne_members');
    return saved ? JSON.parse(saved) : [];
  });

  // Save to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('vivonne_events', JSON.stringify(events));
  }, [events]);

  useEffect(() => {
    localStorage.setItem('vivonne_gallery', JSON.stringify(gallery));
  }, [gallery]);

  useEffect(() => {
    localStorage.setItem('vivonne_newsletters', JSON.stringify(newsletters));
  }, [newsletters]);

  useEffect(() => {
    localStorage.setItem('vivonne_announcements', JSON.stringify(announcements));
  }, [announcements]);

  useEffect(() => {
    localStorage.setItem('vivonne_members', JSON.stringify(members));
  }, [members]);

  return (
    <DataContext.Provider value={{ events, setEvents, gallery, setGallery, newsletters, setNewsletters, announcements, setAnnouncements, members, setMembers }}>
      {children}
    </DataContext.Provider>
  );
};

// ============ Auth Provider ============
const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    const saved = localStorage.getItem('vivonne_user');
    return saved ? JSON.parse(saved) : null;
  });

  const login = async (email: string, password: string): Promise<boolean> => {
    // Admin login
    if ((email === 'jessica@vivonnesf.com' || email === 'jessica@sfmommeetups.com') && password === 'admin123') {
      const adminUser: User = { id: '1', email, name: 'Jessica Wolf', isAdmin: true };
      setUser(adminUser);
      localStorage.setItem('vivonne_user', JSON.stringify(adminUser));
      return true;
    }
    // Demo member logins
    const demoMembers: { [key: string]: User } = {
      'sarah@demo.com': { id: '101', email: 'sarah@demo.com', name: 'Sarah Chen', isAdmin: false, neighborhood: 'Pacific Heights', joinedAt: '2026-01-15', eventsAttended: 5 },
      'lisa@demo.com': { id: '102', email: 'lisa@demo.com', name: 'Lisa Kim', isAdmin: false, neighborhood: 'Marina', joinedAt: '2025-12-01', eventsAttended: 8 },
      'amanda@demo.com': { id: '103', email: 'amanda@demo.com', name: 'Amanda Peters', isAdmin: false, neighborhood: 'Noe Valley', joinedAt: '2025-11-20', eventsAttended: 12 },
    };
    if (demoMembers[email] && password === 'mom123') {
      const member = demoMembers[email];
      setUser(member);
      localStorage.setItem('vivonne_user', JSON.stringify(member));
      // Also add to members list if not exists
      const savedMembers = localStorage.getItem('vivonne_members');
      const members = savedMembers ? JSON.parse(savedMembers) : [];
      if (!members.find((m: User) => m.email === email)) {
        members.push(member);
        localStorage.setItem('vivonne_members', JSON.stringify(members));
      }
      return true;
    }
    // Regular new user
    const regularUser: User = { id: Date.now().toString(), email, name: email.split('@')[0], isAdmin: false, joinedAt: new Date().toISOString() };
    setUser(regularUser);
    localStorage.setItem('vivonne_user', JSON.stringify(regularUser));
    return true;
  };

  const register = async (name: string, email: string, password: string): Promise<boolean> => {
    const newUser: User = { id: Date.now().toString(), email, name, isAdmin: false, joinedAt: new Date().toISOString(), eventsAttended: 0 };
    setUser(newUser);
    localStorage.setItem('vivonne_user', JSON.stringify(newUser));
    // Add to members list
    const savedMembers = localStorage.getItem('vivonne_members');
    const members = savedMembers ? JSON.parse(savedMembers) : [];
    if (!members.find((m: User) => m.email === email)) {
      members.push(newUser);
      localStorage.setItem('vivonne_members', JSON.stringify(members));
    }
    return true;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('vivonne_user');
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============ AI Chatbot Component ============
const AIChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: 'Hi! I\'m your Vivonne assistant. I can help you manage events, write content, answer questions about your community, or brainstorm ideas. What can I help with?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: `You are a helpful assistant for Vivonne SF, a mom meetup community in San Francisco run by Jessica Wolf. You help with:
- Writing event descriptions and newsletters
- Brainstorming event ideas (supper clubs, park hangs, coffee meetups, wine nights, etc.)
- Answering questions about running a mom community
- Providing marketing and growth advice
- General admin tasks and organization
Be friendly, concise, and helpful. Use a warm, approachable tone.` },
            ...messages.map(m => ({ role: m.role, content: m.content })),
            { role: 'user', content: userMessage }
          ],
          max_tokens: 800
        })
      });
      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.choices[0].message.content }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    }
    setLoading(false);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="chatbot-fab"
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'var(--gradient)',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(166, 127, 130, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          zIndex: 1000,
          transition: 'transform 0.2s'
        }}
      >
        <Bot size={28} />
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      right: '24px',
      width: '380px',
      height: '500px',
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      overflow: 'hidden'
    }}>
      <div style={{
        padding: '16px 20px',
        background: 'var(--gradient)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Bot size={24} />
          <div>
            <div style={{ fontWeight: 600 }}>Vivonne Assistant</div>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>AI-powered help</div>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
          <Minimize2 size={20} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            maxWidth: '85%',
            padding: '10px 14px',
            borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
            background: msg.role === 'user' ? 'var(--gradient)' : 'var(--cream)',
            color: msg.role === 'user' ? 'white' : 'var(--text-dark)',
            fontSize: '14px',
            lineHeight: 1.5
          }}>
            {msg.content}
          </div>
        ))}
        {loading && (
          <div style={{ alignSelf: 'flex-start', padding: '10px 14px', background: 'var(--cream)', borderRadius: '16px' }}>
            <Loader size={16} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', display: 'flex', gap: '8px' }}>
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && sendMessage()}
          placeholder="Ask anything..."
          style={{
            flex: 1,
            padding: '10px 14px',
            border: '1px solid #ddd',
            borderRadius: '20px',
            fontSize: '14px',
            outline: 'none'
          }}
        />
        <button
          onClick={sendMessage}
          disabled={loading}
          style={{
            padding: '10px 16px',
            background: 'var(--gradient)',
            border: 'none',
            borderRadius: '20px',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          <Send size={18} />
        </button>
      </div>
    </div>
  );
};

// ============ Image Upload Component ============
const ImageUpload: React.FC<{ value: string; onChange: (url: string) => void; label?: string }> = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const base64 = await convertToBase64(file);
      onChange(base64);
    } catch (error) {
      console.error('Upload error:', error);
    }
    setUploading(false);
  };

  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div 
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '2px dashed var(--primary)',
          borderRadius: 'var(--radius-lg)',
          padding: value ? '8px' : '40px 20px',
          textAlign: 'center',
          cursor: 'pointer',
          background: 'var(--cream)',
          transition: 'all 0.2s'
        }}
      >
        <input 
          ref={fileInputRef}
          type="file" 
          accept="image/*" 
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
        {uploading ? (
          <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
        ) : value ? (
          <img src={value} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }} />
        ) : (
          <div>
            <Upload size={32} color="var(--primary-dark)" />
            <p style={{ marginTop: '8px', color: 'var(--text-muted)' }}>Click to upload image</p>
          </div>
        )}
      </div>
      <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
        <input
          type="url"
          className="form-input"
          value={value.startsWith('data:') ? '' : value}
          onChange={e => onChange(e.target.value)}
          placeholder="Or paste image URL..."
          style={{ fontSize: '13px' }}
        />
      </div>
    </div>
  );
};

// ============ AI Content Generator ============
const AIContentGenerator: React.FC<{ 
  onGenerate: (content: string) => void; 
  placeholder: string;
  context?: string;
}> = ({ onGenerate, placeholder, context }) => {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    const fullPrompt = context ? `${context}\n\nRequest: ${prompt}` : prompt;
    const result = await generateWithAI(fullPrompt);
    onGenerate(result);
    setGenerating(false);
    setPrompt('');
  };

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #f8f4ff 0%, #fff4f4 100%)', 
      padding: '16px', 
      borderRadius: 'var(--radius-md)',
      marginBottom: '16px',
      border: '1px solid #e8e0f0'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <Sparkles size={18} color="var(--primary-dark)" />
        <span style={{ fontWeight: 600, fontSize: '14px' }}>AI Content Generator</span>
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          type="text"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyPress={e => e.key === 'Enter' && generate()}
          placeholder={placeholder}
          className="form-input"
          style={{ flex: 1, fontSize: '14px' }}
        />
        <button 
          onClick={generate} 
          disabled={generating}
          className="btn btn-primary btn-small"
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          {generating ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={16} />}
          Generate
        </button>
      </div>
    </div>
  );
};

// ============ Stripe Checkout ============
const StripeCheckout: React.FC<{ event: Event; onSuccess: () => void; onClose: () => void }> = ({ event, onSuccess, onClose }) => {
  const [processing, setProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const { user } = useAuth();

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing(true);
    
    // Simulate Stripe payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // In production, this would:
    // 1. Create a PaymentIntent on your server
    // 2. Confirm payment with Stripe.js
    // 3. Handle the result
    
    setProcessing(false);
    onSuccess();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Complete Payment</h2>
          <button className="modal-close" onClick={onClose}><X size={24} /></button>
        </div>
        <form onSubmit={handlePayment}>
          <div className="modal-body">
            <div style={{ background: 'var(--cream)', padding: '16px', borderRadius: 'var(--radius-md)', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span>{event.title}</span>
                <span>${event.price.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: 'var(--text-muted)', fontSize: '14px' }}>
                <span>Processing fee</span>
                <span>$2.00</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, paddingTop: '8px', borderTop: '1px solid #ddd' }}>
                <span>Total</span>
                <span>${(event.price + 2).toFixed(2)}</span>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', padding: '12px', background: '#f0fdf4', borderRadius: 'var(--radius-md)' }}>
              <CreditCard size={20} color="#16a34a" />
              <span style={{ fontSize: '14px', color: '#16a34a' }}>Secure payment powered by Stripe</span>
            </div>
            
            <div className="form-group">
              <label className="form-label">Card Number</label>
              <input 
                type="text" 
                className="form-input" 
                value={cardNumber}
                onChange={e => setCardNumber(e.target.value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim())}
                placeholder="4242 4242 4242 4242" 
                maxLength={19}
                required 
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Expiry Date</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={expiry}
                  onChange={e => {
                    let val = e.target.value.replace(/\D/g, '');
                    if (val.length >= 2) val = val.slice(0, 2) + '/' + val.slice(2);
                    setExpiry(val.slice(0, 5));
                  }}
                  placeholder="MM/YY" 
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">CVC</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={cvc}
                  onChange={e => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="123" 
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Email for Confirmation</label>
              <input type="email" className="form-input" defaultValue={user?.email} required />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={processing}>
              {processing ? (
                <>
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  Processing...
                </>
              ) : (
                <>Pay ${(event.price + 2).toFixed(2)}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ Components ============

// Navbar
const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="logo">
          <div className="logo-icon">
            <Heart size={20} />
          </div>
          Vivonne SF
        </Link>
        <div className="nav-links">
          <Link to="/events" className="nav-link">Events</Link>
          <Link to="/gallery" className="nav-link">Photos</Link>
          <Link to="/about" className="nav-link">About</Link>
          {user ? (
            <>
              {user.isAdmin && <Link to="/admin" className="nav-link">Dashboard</Link>}
              <button onClick={() => { logout(); navigate('/'); }} className="btn btn-outline btn-small">
                <LogOut size={16} /> Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="nav-link">Login</Link>
              <Link to="/register" className="btn btn-primary btn-small">Join</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

// Footer
const Footer: React.FC = () => (
  <footer className="footer">
    <div className="footer-grid">
      <div className="footer-column">
        <h4>Vivonne SF</h4>
        <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.8, fontSize: '0.95rem' }}>
          Helping moms in San Francisco meet new friends, bond over good events, and build 
          real community. Come hang with us!
        </p>
      </div>
      <div className="footer-column">
        <h4>Links</h4>
        <ul className="footer-links">
          <li><Link to="/events">Events</Link></li>
          <li><Link to="/gallery">Photos</Link></li>
          <li><Link to="/about">About</Link></li>
          <li><Link to="/contact">Contact</Link></li>
        </ul>
      </div>
      <div className="footer-column">
        <h4>Event Types</h4>
        <ul className="footer-links">
          <li><Link to="/events">Park Hangs</Link></li>
          <li><Link to="/events">Supper Clubs</Link></li>
          <li><Link to="/events">Coffee Meetups</Link></li>
          <li><Link to="/events">Moms Night Out</Link></li>
        </ul>
      </div>
      <div className="footer-column">
        <h4>Follow Along</h4>
        <ul className="footer-links">
          <li><a href="https://instagram.com" target="_blank" rel="noopener noreferrer">Instagram</a></li>
          <li><a href="https://facebook.com" target="_blank" rel="noopener noreferrer">Facebook</a></li>
          <li><a href="mailto:hello@vivonnesf.com">hello@vivonnesf.com</a></li>
        </ul>
      </div>
    </div>
    <div className="footer-bottom">
      <p>© 2026 Vivonne SF · San Francisco, California</p>
    </div>
  </footer>
);

// Home Page
const HomePage: React.FC = () => {
  const { events, announcements } = useData();
  const activeAnnouncement = announcements.find(a => a.active);

  return (
    <>
      {activeAnnouncement && (
        <div className="announcement-banner">
          <span className="announcement-text">{activeAnnouncement.message}</span>
        </div>
      )}
      
      <section className="hero">
        <div className="hero-content">
          <p style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '1rem', opacity: 0.9 }}>San Francisco</p>
          <h1>Meet Your Mom Friends</h1>
          <p>Fun events, real connections, and a community of moms who get it. Find your people in SF.</p>
          <div className="hero-buttons">
            <Link to="/events" className="btn btn-primary btn-large">
              See What's Coming Up <ChevronRight size={20} />
            </Link>
            <Link to="/register" className="btn btn-secondary btn-large">
              Join Us
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="section-title">
          <h2>What We're About</h2>
          <p>Making it easy to meet other moms and actually have fun doing it</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">
              <Users size={28} />
            </div>
            <h3>Real Connections</h3>
            <p>Meet moms in your neighborhood who are in the same stage of life. Just genuine friendships waiting to happen.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <Calendar size={28} />
            </div>
            <h3>Fun Events</h3>
            <p>Park hangs, coffee dates, yoga, supper clubs, wine nights. We plan it so you can just show up.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">
              <MapPin size={28} />
            </div>
            <h3>All Over SF</h3>
            <p>Events across the city at the best kid-friendly cafés, parks, restaurants, and sometimes we escape to Napa. Variety is the spice of mom life.</p>
          </div>
        </div>
      </section>

      {events.length > 0 && (
      <section className="section" style={{ background: 'white' }}>
        <div className="section-title">
          <h2>Upcoming Events</h2>
          <p>Here's what we've got on the calendar</p>
        </div>
        <div className="events-grid">
          {events.slice(0, 3).map(event => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <Link to="/events" className="btn btn-primary">See All Events</Link>
        </div>
      </section>
      )}

      <section className="section">
        <div className="section-title">
          <h2>What Moms Are Saying</h2>
          <p>Don't take our word for it</p>
        </div>
        <div className="features-grid">
          <div className="testimonial-card">
            <p className="testimonial-text">"I moved to SF not knowing anyone with kids. Within a month of joining, I had a whole group of mom friends. We text daily now and our kids are obsessed with each other."</p>
            <div className="testimonial-author">
              <div className="testimonial-avatar">SC</div>
              <div>
                <div className="testimonial-name">Sarah C.</div>
                <div className="testimonial-role">Mom of 2 | Pacific Heights</div>
              </div>
            </div>
          </div>
          <div className="testimonial-card">
            <p className="testimonial-text">"The events are actually fun. Not just standing around at a playground. I look forward to them every month!"</p>
            <div className="testimonial-author">
              <div className="testimonial-avatar">LH</div>
              <div>
                <div className="testimonial-name">Lauren H.</div>
                <div className="testimonial-role">Mom of 1 | Marina</div>
              </div>
            </div>
          </div>
          <div className="testimonial-card">
            <p className="testimonial-text">"Finally found my people! It's so nice to be around other moms who want to do more than just talk about sleep schedules. Real friendships, fun outings. 10/10."</p>
            <div className="testimonial-author">
              <div className="testimonial-avatar">AP</div>
              <div>
                <div className="testimonial-name">Amanda P.</div>
                <div className="testimonial-role">Mom of 3 | Noe Valley</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section newsletter-section">
        <div className="section-title">
          <h2>Stay in the Loop</h2>
          <p>Get event updates and first dibs on popular events (supper clubs sell out fast!)</p>
        </div>
        <form className="newsletter-form" onSubmit={(e) => { e.preventDefault(); alert('You\'re in! Check your inbox for a welcome email.'); }}>
          <input type="email" className="form-input" placeholder="Your email" required />
          <button type="submit" className="btn btn-primary">
            <Send size={18} /> Subscribe
          </button>
        </form>
      </section>
    </>
  );
};

// Event Card
const EventCard: React.FC<{ event: Event }> = ({ event }) => {
  const spotsLeft = event.capacity - event.registered;
  
  return (
    <div className="card">
      <img src={event.image} alt={event.title} className="card-image" />
      <div className="card-content">
        <span className="card-tag">{event.category}</span>
        <h3 className="card-title">{event.title}</h3>
        <p className="card-description">{event.description}</p>
        <div className="card-meta">
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Calendar size={16} /> {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Clock size={16} /> {event.time}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Users size={16} /> {spotsLeft} spots
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
          <span className="price">${event.price}</span>
          <Link to={`/events/${event.id}`} className="btn btn-primary btn-small">Register</Link>
        </div>
      </div>
    </div>
  );
};

// Events Page
const EventsPage: React.FC = () => {
  const { events } = useData();
  const [filter, setFilter] = useState('all');
  const categories = ['all', 'Outdoor', 'Wellness', 'Social', 'Educational', 'Adults Only'];
  
  const filteredEvents = filter === 'all' 
    ? events 
    : events.filter(e => e.category === filter);

  return (
    <div className="section">
      <div className="section-title">
        <p style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '0.5rem', color: 'var(--primary-dark)' }}>Events</p>
        <h2>What's Coming Up</h2>
        <p>Grab a spot and come hang with us!</p>
      </div>
      
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '2.5rem', flexWrap: 'wrap' }}>
        {categories.map(cat => (
          <button
            key={cat}
            onClick={() => setFilter(cat)}
            className={`btn ${filter === cat ? 'btn-primary' : 'btn-outline'} btn-small`}
          >
            {cat === 'all' ? 'All Events' : cat}
          </button>
        ))}
      </div>
      
      <div className="events-grid">
        {filteredEvents.map(event => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    </div>
  );
};

// Event Detail Page
const EventDetailPage: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { events } = useData();
  const event = events.find(e => e.id === id);
  const [showCheckout, setShowCheckout] = useState(false);
  const [registered, setRegistered] = useState(false);
  
  if (!event) {
    return <div className="section"><h2>Event not found</h2></div>;
  }
  
  const spotsLeft = event.capacity - event.registered;

  const handleSuccess = () => {
    setShowCheckout(false);
    setRegistered(true);
  };

  if (registered) {
    return (
      <div className="section" style={{ textAlign: 'center', maxWidth: '500px', margin: '0 auto' }}>
        <div style={{ width: '80px', height: '80px', background: 'var(--success)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
          <CheckCircle size={40} color="white" />
        </div>
        <h2>You're Registered!</h2>
        <p style={{ color: 'var(--text-muted)', margin: '1rem 0 2rem' }}>
          Confirmation email sent to {user?.email}. We can't wait to see you at {event.title}!
        </p>
        <Link to="/events" className="btn btn-primary">Back to Events</Link>
      </div>
    );
  }

  return (
    <div className="event-detail">
      <img src={event.image} alt={event.title} className="event-detail-image" />
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '2rem' }}>
        <div>
          <span className="card-tag" style={{ marginBottom: '1rem', display: 'inline-block' }}>{event.category}</span>
          <h1 className="event-detail-title">{event.title}</h1>
          
          <div className="event-detail-meta" style={{ marginBottom: '2rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={20} /> {new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={20} /> {event.time}
            </span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem', color: 'var(--text-muted)' }}>
            <MapPin size={20} /> {event.location}
          </div>
          
          <div className="event-detail-content" dangerouslySetInnerHTML={{ __html: event.content }} />
        </div>
        
        <div className="event-sidebar">
          <div className="price" style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>${event.price}</div>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>per person</p>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Users size={18} />
            <span>{spotsLeft} of {event.capacity} spots remaining</span>
          </div>
          
          <div style={{ height: '8px', background: 'var(--cream)', borderRadius: '4px', marginBottom: '1.5rem' }}>
            <div style={{ 
              height: '100%', 
              background: 'var(--gradient)', 
              borderRadius: '4px',
              width: `${(event.registered / event.capacity) * 100}%`
            }} />
          </div>
          
          {user ? (
            <button onClick={() => setShowCheckout(true)} className="btn btn-primary" style={{ width: '100%' }}>
              <CreditCard size={18} /> Register & Pay
            </button>
          ) : (
            <Link to="/login" className="btn btn-primary" style={{ width: '100%', display: 'block', textAlign: 'center' }}>
              Login to Register
            </Link>
          )}
          
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '12px', textAlign: 'center' }}>
            Secure payment via Stripe
          </p>
        </div>
      </div>
      
      {showCheckout && (
        <StripeCheckout event={event} onSuccess={handleSuccess} onClose={() => setShowCheckout(false)} />
      )}
    </div>
  );
};

// Gallery Page
const GalleryPage: React.FC = () => {
  const { gallery } = useData();
  
  return (
    <div className="section">
      <div className="section-title">
        <p style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '0.5rem', color: 'var(--primary-dark)' }}>Photos</p>
        <h2>Pics from Our Events</h2>
        <p>See what we've been up to!</p>
      </div>
      {gallery.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: 'var(--radius-lg)' }}>
          <Image size={48} color="var(--text-muted)" style={{ marginBottom: '16px', opacity: 0.5 }} />
          <p style={{ color: 'var(--text-muted)' }}>Photos coming soon!</p>
        </div>
      ) : (
        <div className="gallery-grid">
          {gallery.map(img => (
            <div key={img.id} className="gallery-item">
              <img src={img.url} alt={img.caption} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// About Page
const AboutPage: React.FC = () => (
  <div className="section">
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div className="section-title">
        <p style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '0.5rem', color: 'var(--primary-dark)' }}>About</p>
        <h2>Hey, We're Vivonne!</h2>
      </div>
      
      <div style={{ background: 'white', padding: '3rem', borderRadius: 'var(--radius-lg)', marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>How It Started</h3>
        <p style={{ color: 'var(--text-muted)', lineHeight: 2, marginBottom: '1.5rem', fontSize: '1.05rem' }}>
          Real talk: making mom friends as an adult is hard. You're exhausted, your schedule is chaos, 
          and standing around at a playground hoping to spark a conversation feels awkward. We get it. 
          because that's exactly where this started.
        </p>
        <p style={{ color: 'var(--text-muted)', lineHeight: 2, fontSize: '1.05rem' }}>
          What began as a few moms grabbing coffee has turned into a real community. Now we've got 
          park hangs, supper clubs, yoga sessions, wine nights, and everything in between. The goal 
          is simple: make it easy to meet cool moms and actually have fun while doing it.
        </p>
      </div>
      
      <div style={{ background: 'white', padding: '3rem', borderRadius: 'var(--radius-lg)', marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>What We're About</h3>
        <p style={{ color: 'var(--text-muted)', lineHeight: 2, marginBottom: '1.5rem', fontSize: '1.05rem' }}>
          We plan events so you don't have to. Show up, meet people, have a good time. That's it. 
          No pressure, no awkwardness, no MLM pitches (we promise).
        </p>
        <p style={{ color: 'var(--text-muted)', lineHeight: 2, fontSize: '1.05rem' }}>
          We do a mix of kid-friendly stuff (park days, museum trips, coffee mornings) and 
          adults-only events (supper clubs, wine tastings, actual dinners where you can finish 
          a sentence). Because being a mom is amazing, but sometimes you need a break.
        </p>
      </div>
      
      <div style={{ background: 'white', padding: '3rem', borderRadius: 'var(--radius-lg)' }}>
        <h3 style={{ marginBottom: '1.5rem', fontSize: '1.5rem' }}>Meet Jessica</h3>
        <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-start' }}>
          <div style={{ width: '160px', height: '160px', borderRadius: '50%', background: 'var(--gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '3rem', fontFamily: 'Playfair Display', flexShrink: 0 }}>
            JW
          </div>
          <div>
            <p style={{ color: 'var(--text-muted)', lineHeight: 2, fontSize: '1.1rem', fontStyle: 'italic', marginBottom: '1.5rem' }}>
              "I started this because I was tired of feeling like the only mom who wanted to do more 
              than talk about sleep training. I wanted friends who'd grab wine on a Tuesday, take the 
              kids to weird museums, or just sit in a park and vent. Turns out a lot of moms wanted 
              the same thing. So here we are!"
            </p>
            <p style={{ fontWeight: 600, fontSize: '1rem' }}>Jessica Wolf</p>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Founder</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Contact Page
const ContactPage: React.FC = () => (
  <div className="section">
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      <div className="section-title">
        <p style={{ fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '3px', marginBottom: '0.5rem', color: 'var(--primary-dark)' }}>Contact</p>
        <h2>Say Hi!</h2>
        <p>Questions? Ideas for events? Just want to chat? We'd love to hear from you.</p>
      </div>
      
      <div style={{ background: 'white', padding: '2.5rem', borderRadius: 'var(--radius-lg)' }}>
        <form onSubmit={e => { e.preventDefault(); alert('Got it! We\'ll get back to you soon.'); }}>
          <div className="form-group">
            <label className="form-label">Your Name</label>
            <input type="text" className="form-input" placeholder="What's your name?" required />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input type="email" className="form-input" placeholder="you@example.com" required />
          </div>
          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea className="form-textarea" rows={5} placeholder="What's up?" required />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Send</button>
        </form>
      </div>
      
      <div style={{ textAlign: 'center', marginTop: '2rem', color: 'var(--text-muted)' }}>
        <p style={{ marginBottom: '0.5rem' }}>Or just email us at</p>
        <a href="mailto:hello@vivonnesf.com" style={{ color: 'var(--primary-dark)', fontWeight: 500 }}>hello@vivonnesf.com</a>
      </div>
    </div>
  </div>
);

// Login Page
const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('jessica@vivonnesf.com');
  const [password, setPassword] = useState('admin123');
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const success = await login(email, password);
    if (success) {
      navigate('/events');
    } else {
      setError('Invalid credentials');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Welcome Back!</h1>
        <p className="subtitle">Sign in to see events and register</p>
        
        {error && <div style={{ color: 'var(--error)', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input 
              type="email" 
              className="form-input" 
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required 
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input 
              type="password" 
              className="form-input" 
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required 
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Sign In</button>
        </form>
        
        <p className="auth-footer">
          New here? <Link to="/register">Sign up</Link>
        </p>
        
        <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--cream)', borderRadius: 'var(--radius-md)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          <strong>Demo Logins:</strong><br />
          <span style={{ display: 'block', marginTop: '8px' }}>Admin: jessica@vivonnesf.com / admin123</span>
          <span style={{ display: 'block', marginTop: '4px' }}>Member: sarah@demo.com / mom123</span>
          <span style={{ display: 'block', marginTop: '4px' }}>Member: lisa@demo.com / mom123</span>
        </div>
      </div>
    </div>
  );
};

// Register Page
const RegisterPage: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await register(name, email, password);
    if (success) {
      navigate('/events');
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Join Vivonne SF</h1>
        <p className="subtitle">Create an account to register for events and meet other moms!</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Your Name</label>
            <input 
              type="text" 
              className="form-input" 
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="What should we call you?"
              required 
            />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input 
              type="email" 
              className="form-input" 
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required 
            />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <input 
              type="password" 
              className="form-input" 
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Create a password"
              required 
            />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }}>Sign Up</button>
        </form>
        
        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

// ============ ADMIN COMPONENTS ============

// Admin Dashboard
const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/');
    }
  }, [user, navigate]);

  if (!user?.isAdmin) return null;

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <nav>
          {[
            { id: 'overview', icon: BarChart3, label: 'Overview' },
            { id: 'events', icon: Calendar, label: 'Events' },
            { id: 'members', icon: Users, label: 'Members' },
            { id: 'newsletters', icon: Mail, label: 'Newsletters' },
            { id: 'announcements', icon: Bell, label: 'Announcements' },
            { id: 'gallery', icon: Image, label: 'Gallery' },
            { id: 'payments', icon: DollarSign, label: 'Payments' },
            { id: 'content', icon: FileText, label: 'Site Content' },
            { id: 'settings', icon: Settings, label: 'Settings' },
          ].map(item => (
            <button
              key={item.id}
              className={`admin-nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => setActiveTab(item.id)}
              style={{ cursor: 'pointer', background: 'none', border: 'none', width: '100%', textAlign: 'left', font: 'inherit' }}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </nav>
      </aside>
      
      <main className="admin-content">
        {activeTab === 'overview' && <AdminOverview />}
        {activeTab === 'events' && <AdminEvents />}
        {activeTab === 'members' && <AdminMembers />}
        {activeTab === 'newsletters' && <AdminNewsletters />}
        {activeTab === 'announcements' && <AdminAnnouncements />}
        {activeTab === 'gallery' && <AdminGallery />}
        {activeTab === 'payments' && <AdminPayments />}
        {activeTab === 'content' && <AdminSiteContent />}
        {activeTab === 'settings' && <AdminSettings />}
      </main>

      <AIChatbot />
    </div>
  );
};

// Admin Overview
const AdminOverview: React.FC = () => {
  const { events, members, gallery } = useData();
  const isNewAccount = events.length === 0 && members.length === 0;

  if (isNewAccount) {
    return (
      <div>
        <div className="admin-header">
          <h1>Welcome to Vivonne SF!</h1>
        </div>
        
        <div style={{ background: 'linear-gradient(135deg, #fef7f7 0%, #f0f9ff 100%)', borderRadius: 'var(--radius-lg)', padding: '32px', marginBottom: '24px', border: '1px solid #e8d5d6' }}>
          <h2 style={{ marginBottom: '8px', fontSize: '24px' }}>Quick Start Guide</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Get your community up and running in just a few steps.</p>
          
          <div style={{ display: 'grid', gap: '16px' }}>
            {[
              { step: 1, title: 'Create Your First Event', description: 'Add an event like a coffee meetup, park day, or supper club. Use AI to help write descriptions!', link: 'events', icon: Calendar },
              { step: 2, title: 'Add Photos to Gallery', description: 'Upload photos from past events or add some placeholder images to make the site feel alive.', link: 'gallery', icon: Image },
              { step: 3, title: 'Set Up Announcements', description: 'Create a banner announcement to highlight upcoming events or welcome new visitors.', link: 'announcements', icon: Bell },
              { step: 4, title: 'Customize Site Content', description: 'Edit your homepage text, about page, and testimonials using the Site Content editor.', link: 'content', icon: FileText },
              { step: 5, title: 'Configure Settings', description: 'Add your email service (Mailchimp, etc.) and Stripe keys for payments.', link: 'settings', icon: Settings },
            ].map(item => (
              <div key={item.step} style={{ display: 'flex', gap: '16px', padding: '20px', background: 'white', borderRadius: 'var(--radius-md)', alignItems: 'flex-start' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--gradient)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, flexShrink: 0 }}>
                  {item.step}
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ marginBottom: '4px', fontSize: '16px' }}>{item.title}</h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: 0, lineHeight: 1.5 }}>{item.description}</p>
                </div>
                <item.icon size={20} color="var(--primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <Sparkles size={20} color="var(--primary)" />
            <h3 style={{ margin: 0 }}>Pro Tip: Use the AI Assistant</h3>
          </div>
          <p style={{ color: 'var(--text-muted)', margin: 0, lineHeight: 1.6 }}>
            Click the chat button in the bottom right corner anytime you need help writing event descriptions, 
            newsletters, or brainstorming ideas. The AI knows all about Vivonne SF and can help you create 
            engaging content quickly.
          </p>
        </div>
      </div>
    );
  }

  const nextEvent = events.length > 0 ? events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())[0] : null;

  return (
    <div>
      <div className="admin-header">
        <h1>Dashboard</h1>
        <span style={{ color: 'var(--text-muted)' }}>Welcome back, Jessica!</span>
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="stat-value">{members.length}</div>
              <div className="stat-label">Total Members</div>
            </div>
            <Users size={24} color="var(--primary)" />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="stat-value">{events.length}</div>
              <div className="stat-label">Events</div>
            </div>
            <Calendar size={24} color="var(--primary)" />
          </div>
          {nextEvent && <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-muted)' }}>Next: {new Date(nextEvent.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>}
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="stat-value">{gallery.length}</div>
              <div className="stat-label">Gallery Photos</div>
            </div>
            <Image size={24} color="var(--primary)" />
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div className="stat-value">{events.length > 0 ? Math.round(events.reduce((acc, e) => acc + (e.registered / e.capacity * 100), 0) / events.length) : 0}%</div>
              <div className="stat-label">Avg Fill Rate</div>
            </div>
            <TrendingUp size={24} color="var(--primary)" />
          </div>
        </div>
      </div>
      
      {events.length > 0 && (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
        <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={20} /> Upcoming Events
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {events.slice(0, 3).map(event => (
              <div key={event.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'var(--cream)', borderRadius: 'var(--radius-md)' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{event.title}</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{new Date(event.date).toLocaleDateString()}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 600 }}>{event.registered}/{event.capacity}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>registered</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '1.5rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={20} /> Quick Actions
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <button className="btn btn-outline" style={{ justifyContent: 'flex-start', gap: '12px' }}>
              <Plus size={18} /> Create New Event
            </button>
            <button className="btn btn-outline" style={{ justifyContent: 'flex-start', gap: '12px' }}>
              <Mail size={18} /> Send Newsletter
            </button>
            <button className="btn btn-outline" style={{ justifyContent: 'flex-start', gap: '12px' }}>
              <ImagePlus size={18} /> Add Photos
            </button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

// Admin Events
const AdminEvents: React.FC = () => {
  const { events, setEvents } = useData();
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  const handleSave = (event: Event) => {
    if (editingEvent) {
      setEvents(events.map(e => e.id === event.id ? event : e));
    } else {
      setEvents([...events, { ...event, id: Date.now().toString() }]);
    }
    setShowModal(false);
    setEditingEvent(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      setEvents(events.filter(e => e.id !== id));
    }
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Events</h1>
        <button className="btn btn-primary" onClick={() => { setEditingEvent(null); setShowModal(true); }}>
          <Plus size={18} /> Create Event
        </button>
      </div>
      
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Date</th>
              <th>Price</th>
              <th>Registered</th>
              <th>Revenue</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map(event => (
              <tr key={event.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <img src={event.image} alt="" style={{ width: '50px', height: '50px', borderRadius: 'var(--radius-sm)', objectFit: 'cover' }} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{event.title}</div>
                      <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{event.category}</div>
                    </div>
                  </div>
                </td>
                <td>{new Date(event.date).toLocaleDateString()}</td>
                <td>${event.price}</td>
                <td>
                  <span style={{ 
                    padding: '4px 8px', 
                    borderRadius: '12px', 
                    fontSize: '13px',
                    background: event.registered >= event.capacity ? '#fee2e2' : event.registered >= event.capacity * 0.8 ? '#fef3c7' : '#dcfce7',
                    color: event.registered >= event.capacity ? '#b91c1c' : event.registered >= event.capacity * 0.8 ? '#b45309' : '#166534'
                  }}>
                    {event.registered}/{event.capacity}
                  </span>
                </td>
                <td style={{ fontWeight: 500 }}>${event.registered * event.price}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-outline btn-small" onClick={() => { setEditingEvent(event); setShowModal(true); }}>
                      <Edit size={16} />
                    </button>
                    <button className="btn btn-outline btn-small" onClick={() => handleDelete(event.id)} style={{ color: 'var(--error)' }}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {showModal && (
        <EventModal 
          event={editingEvent} 
          onSave={handleSave} 
          onClose={() => { setShowModal(false); setEditingEvent(null); }} 
        />
      )}
    </div>
  );
};

// Event Modal with AI
const EventModal: React.FC<{ event: Event | null; onSave: (event: Event) => void; onClose: () => void }> = ({ event, onSave, onClose }) => {
  const [formData, setFormData] = useState<Partial<Event>>(event || {
    title: '',
    description: '',
    content: '',
    date: '',
    time: '',
    location: '',
    price: 0,
    capacity: 20,
    registered: 0,
    image: '',
    category: 'Social'
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData as Event);
  };

  const handleAIGenerate = (content: string) => {
    // Parse AI response for description and content
    if (content.includes('Description:')) {
      const parts = content.split('Full Description:');
      const desc = parts[0].replace('Description:', '').trim();
      const full = parts[1]?.trim() || '';
      setFormData({ ...formData, description: desc.slice(0, 200), content: full });
    } else {
      setFormData({ ...formData, description: content.slice(0, 200), content: content });
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>{event ? 'Edit Event' : 'Create Event'}</h2>
          <button className="modal-close" onClick={onClose}><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <AIContentGenerator
              onGenerate={handleAIGenerate}
              placeholder="e.g., Write a fun description for a supper club dinner party..."
              context={`Event type: ${formData.category}. Event title: ${formData.title || 'Untitled'}. Location: ${formData.location || 'TBD'}. Generate a short description (1-2 sentences) and a longer full description with bullet points for the event details.`}
            />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Event Title</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Park Hangout at Dolores"
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select 
                  className="form-select"
                  value={formData.category}
                  onChange={e => setFormData({ ...formData, category: e.target.value })}
                >
                  <option>Social</option>
                  <option>Outdoor</option>
                  <option>Wellness</option>
                  <option>Educational</option>
                  <option>Adults Only</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Short Description</label>
              <input 
                type="text" 
                className="form-input" 
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief one-liner for event cards"
                required 
              />
            </div>

            <div className="form-group">
              <label className="form-label">Full Description (HTML supported)</label>
              <textarea 
                className="form-textarea" 
                value={formData.content}
                onChange={e => setFormData({ ...formData, content: e.target.value })}
                rows={6}
                placeholder="Detailed event description..."
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input 
                  type="date" 
                  className="form-input" 
                  value={formData.date}
                  onChange={e => setFormData({ ...formData, date: e.target.value })}
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Time</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={formData.time}
                  onChange={e => setFormData({ ...formData, time: e.target.value })}
                  placeholder="10:00 AM"
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Location</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={formData.location}
                  onChange={e => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Dolores Park"
                  required 
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Price ($)</label>
                <input 
                  type="number" 
                  className="form-input" 
                  value={formData.price}
                  onChange={e => setFormData({ ...formData, price: Number(e.target.value) })}
                  required 
                />
              </div>
              <div className="form-group">
                <label className="form-label">Capacity</label>
                <input 
                  type="number" 
                  className="form-input" 
                  value={formData.capacity}
                  onChange={e => setFormData({ ...formData, capacity: Number(e.target.value) })}
                  required 
                />
              </div>
            </div>

            <ImageUpload
              label="Event Image"
              value={formData.image || ''}
              onChange={url => setFormData({ ...formData, image: url })}
            />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">{event ? 'Save Changes' : 'Create Event'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Admin Members
const AdminMembers: React.FC = () => {
  const { members } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMember, setSelectedMember] = useState<User | null>(null);

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.neighborhood?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="admin-header">
        <h1>Members ({members.length})</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search members..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: '250px' }}
          />
          <button className="btn btn-outline">
            <FileText size={18} /> Export CSV
          </button>
        </div>
      </div>
      
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Email</th>
              <th>Neighborhood</th>
              <th>Joined</th>
              <th>Events</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.map(member => (
              <tr key={member.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      borderRadius: '50%', 
                      background: 'var(--gradient)', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '14px'
                    }}>
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <span style={{ fontWeight: 500 }}>{member.name}</span>
                  </div>
                </td>
                <td>{member.email}</td>
                <td>{member.neighborhood || '-'}</td>
                <td>{member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : '-'}</td>
                <td>{member.eventsAttended || 0}</td>
                <td>
                  <button className="btn btn-outline btn-small" onClick={() => setSelectedMember(member)}>
                    <Eye size={16} /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedMember && (
        <div className="modal-overlay" onClick={() => setSelectedMember(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Member Details</h2>
              <button className="modal-close" onClick={() => setSelectedMember(null)}><X size={24} /></button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%', 
                  background: 'var(--gradient)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '28px',
                  margin: '0 auto 12px'
                }}>
                  {selectedMember.name.split(' ').map(n => n[0]).join('')}
                </div>
                <h3>{selectedMember.name}</h3>
                <p style={{ color: 'var(--text-muted)' }}>{selectedMember.neighborhood}</p>
              </div>
              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Email</span>
                  <span>{selectedMember.email}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Phone</span>
                  <span>{selectedMember.phone || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Member Since</span>
                  <span>{selectedMember.joinedAt ? new Date(selectedMember.joinedAt).toLocaleDateString() : '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', background: 'var(--cream)', borderRadius: '8px' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Events Attended</span>
                  <span>{selectedMember.eventsAttended || 0}</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => window.location.href = `mailto:${selectedMember.email}`}>
                <Mail size={16} /> Email
              </button>
              <button className="btn btn-primary" onClick={() => setSelectedMember(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Newsletters with AI
const AdminNewsletters: React.FC = () => {
  const { newsletters, setNewsletters } = useData();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Newsletter | null>(null);

  const handleSave = (newsletter: Newsletter) => {
    if (editing) {
      setNewsletters(newsletters.map(n => n.id === newsletter.id ? newsletter : n));
    } else {
      setNewsletters([{ ...newsletter, id: Date.now().toString(), createdAt: new Date().toISOString() }, ...newsletters]);
    }
    setShowModal(false);
    setEditing(null);
  };

  const handleSend = (id: string) => {
    if (window.confirm('Send this newsletter to all 247 members?')) {
      setNewsletters(newsletters.map(n => n.id === id ? { ...n, sentAt: new Date().toISOString(), recipientCount: 247 } : n));
      alert('Newsletter sent successfully to 247 members!');
    }
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Newsletters</h1>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
          <Plus size={18} /> Create Newsletter
        </button>
      </div>
      
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Status</th>
              <th>Recipients</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {newsletters.map(newsletter => (
              <tr key={newsletter.id}>
                <td style={{ fontWeight: 500 }}>{newsletter.title}</td>
                <td>
                  <span className={`badge ${newsletter.sentAt ? 'badge-success' : 'badge-warning'}`}>
                    {newsletter.sentAt ? 'Sent' : 'Draft'}
                  </span>
                </td>
                <td>{newsletter.recipientCount || '-'}</td>
                <td>{new Date(newsletter.createdAt).toLocaleDateString()}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-outline btn-small" onClick={() => { setEditing(newsletter); setShowModal(true); }}>
                      <Edit size={16} />
                    </button>
                    {!newsletter.sentAt && (
                      <button className="btn btn-primary btn-small" onClick={() => handleSend(newsletter.id)}>
                        <Send size={16} /> Send
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {showModal && (
        <NewsletterModal 
          newsletter={editing}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </div>
  );
};

// Newsletter Modal with AI
const NewsletterModal: React.FC<{ newsletter: Newsletter | null; onSave: (n: Newsletter) => void; onClose: () => void }> = ({ newsletter, onSave, onClose }) => {
  const [title, setTitle] = useState(newsletter?.title || '');
  const [content, setContent] = useState(newsletter?.content || '');
  const [showPreview, setShowPreview] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ 
      id: newsletter?.id || '', 
      title, 
      content, 
      createdAt: newsletter?.createdAt || '',
      sentAt: newsletter?.sentAt 
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>{newsletter ? 'Edit Newsletter' : 'Create Newsletter'}</h2>
          <button className="modal-close" onClick={onClose}><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <AIContentGenerator
              onGenerate={setContent}
              placeholder="e.g., Write a newsletter about our upcoming March events..."
              context="Write an engaging newsletter for Vivonne SF, a mom meetup group. Include a warm greeting, event highlights, and a friendly sign-off. Use HTML formatting."
            />

            <div className="form-group">
              <label className="form-label">Subject Line</label>
              <input 
                type="text" 
                className="form-input" 
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g., March Events Are Here!"
                required 
              />
            </div>
            
            <div className="form-group">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label className="form-label" style={{ margin: 0 }}>Content</label>
                <button 
                  type="button" 
                  className="btn btn-outline btn-small"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  <Eye size={16} /> {showPreview ? 'Edit' : 'Preview'}
                </button>
              </div>
              
              {showPreview ? (
                <div style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: 'var(--radius-md)', 
                  padding: '24px',
                  background: 'white',
                  minHeight: '300px'
                }}>
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                </div>
              ) : (
                <textarea 
                  className="form-textarea" 
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  rows={12}
                  placeholder="<h2>Hey Mamas!</h2><p>Here's what's happening...</p>"
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              )}
            </div>

            <div style={{ background: 'var(--cream)', padding: '16px', borderRadius: 'var(--radius-md)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Mail size={18} color="var(--primary-dark)" />
                <span style={{ fontWeight: 500 }}>Will be sent to 247 members</span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                Recipients will receive this at their registered email addresses.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary">Save Newsletter</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Admin Announcements
const AdminAnnouncements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([
    { id: '1', message: 'March events are live! Supper club spots going fast.', active: true, createdAt: '2026-01-20' }
  ]);
  const [newMessage, setNewMessage] = useState('');

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setAnnouncements([
      { id: Date.now().toString(), message: newMessage, active: true, createdAt: new Date().toISOString() },
      ...announcements.map(a => ({ ...a, active: false }))
    ]);
    setNewMessage('');
  };

  const toggleActive = (id: string) => {
    setAnnouncements(announcements.map(a => ({
      ...a,
      active: a.id === id ? !a.active : false
    })));
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Announcements</h1>
      </div>
      
      <div style={{ background: 'white', padding: '24px', borderRadius: 'var(--radius-lg)', marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px' }}>Add Announcement</h3>
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: '12px' }}>
          <input 
            type="text" 
            className="form-input" 
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder="e.g., New events just dropped! Check them out."
            style={{ flex: 1 }}
          />
          <button type="submit" className="btn btn-primary">
            <Plus size={18} /> Add
          </button>
        </form>
        <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '8px' }}>
          Appears as a banner at the top of the homepage. Only one can be active at a time.
        </p>
      </div>
      
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Message</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {announcements.map(a => (
              <tr key={a.id}>
                <td>{a.message}</td>
                <td>
                  <span className={`badge ${a.active ? 'badge-success' : 'badge-warning'}`}>
                    {a.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                <td>
                  <button 
                    className={`btn ${a.active ? 'btn-outline' : 'btn-primary'} btn-small`}
                    onClick={() => toggleActive(a.id)}
                  >
                    {a.active ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Admin Gallery
const AdminGallery: React.FC = () => {
  const { gallery, setGallery } = useData();
  const [showModal, setShowModal] = useState(false);
  const [newImage, setNewImage] = useState({ url: '', caption: '' });

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newImage.url) return;
    setGallery([...gallery, { id: Date.now().toString(), ...newImage }]);
    setNewImage({ url: '', caption: '' });
    setShowModal(false);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this image?')) {
      setGallery(gallery.filter(img => img.id !== id));
    }
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Gallery</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <ImagePlus size={18} /> Add Photo
        </button>
      </div>
      
      <div className="gallery-grid">
        {gallery.map(img => (
          <div key={img.id} className="gallery-item" style={{ position: 'relative' }}>
            <img src={img.url} alt={img.caption} />
            <button 
              onClick={() => handleDelete(img.id)}
              style={{ 
                position: 'absolute', 
                top: '8px', 
                right: '8px', 
                background: 'white', 
                border: 'none', 
                borderRadius: '50%', 
                padding: '8px',
                cursor: 'pointer',
                boxShadow: 'var(--shadow-md)'
              }}
            >
              <Trash2 size={16} color="var(--error)" />
            </button>
            {img.caption && (
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '12px',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                color: 'white',
                fontSize: '14px'
              }}>
                {img.caption}
              </div>
            )}
          </div>
        ))}
      </div>
      
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Photo</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={24} /></button>
            </div>
            <form onSubmit={handleAdd}>
              <div className="modal-body">
                <ImageUpload
                  label="Photo"
                  value={newImage.url}
                  onChange={url => setNewImage({ ...newImage, url })}
                />
                <div className="form-group">
                  <label className="form-label">Caption (optional)</label>
                  <input 
                    type="text" 
                    className="form-input" 
                    value={newImage.caption}
                    onChange={e => setNewImage({ ...newImage, caption: e.target.value })}
                    placeholder="e.g., Supper Club January 2026"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={!newImage.url}>Add Photo</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Payments
const AdminPayments: React.FC = () => {
  const payments = [
    { id: '1', member: 'Sarah Chen', event: 'Supper Club', amount: 85, date: '2026-02-01', status: 'completed' },
    { id: '2', member: 'Lisa Kim', event: 'Park Hangout', amount: 15, date: '2026-02-01', status: 'completed' },
    { id: '3', member: 'Amanda Peters', event: 'Mom & Baby Yoga', amount: 25, date: '2026-01-30', status: 'completed' },
    { id: '4', member: 'Emily Watson', event: 'Coffee + Catch Up', amount: 18, date: '2026-01-28', status: 'completed' },
    { id: '5', member: 'Rachel Green', event: 'Supper Club', amount: 85, date: '2026-01-25', status: 'refunded' },
  ];

  const totalRevenue = payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0);

  return (
    <div>
      <div className="admin-header">
        <h1>Payments</h1>
        <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="btn btn-outline">
          Open Stripe Dashboard
        </a>
      </div>

      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <div className="stat-value">${totalRevenue}</div>
          <div className="stat-label">Total Revenue (Feb)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{payments.filter(p => p.status === 'completed').length}</div>
          <div className="stat-label">Successful Payments</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">$2.00</div>
          <div className="stat-label">Processing Fee</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">1</div>
          <div className="stat-label">Refunds</div>
        </div>
      </div>
      
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Event</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(p => (
              <tr key={p.id}>
                <td style={{ fontWeight: 500 }}>{p.member}</td>
                <td>{p.event}</td>
                <td>${p.amount}</td>
                <td>{new Date(p.date).toLocaleDateString()}</td>
                <td>
                  <span className={`badge ${p.status === 'completed' ? 'badge-success' : 'badge-warning'}`}>
                    {p.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Admin Site Content Editor
const AdminSiteContent: React.FC = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [generating, setGenerating] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  
  // Load saved content or use defaults
  const [content, setContent] = useState(() => {
    const saved = localStorage.getItem('vivonne_site_content');
    return saved ? JSON.parse(saved) : {
      hero: {
        tagline: 'San Francisco',
        headline: 'Meet Your Mom Friends',
        subtext: 'Fun events, real connections, and a community of moms who get it. Find your people in SF.',
        ctaPrimary: 'See What\'s Coming Up',
        ctaSecondary: 'Join Us'
      },
      about: {
        headline: 'Hey, We\'re Vivonne!',
        story: 'Real talk: making mom friends as an adult is hard. You\'re exhausted, your schedule is chaos, and standing around at a playground hoping to spark a conversation feels awkward. That\'s exactly where this started.\n\nWhat began as a few moms grabbing coffee has turned into a real community. Now we\'ve got park hangs, supper clubs, yoga sessions, wine nights, and everything in between. The goal is simple: make it easy to meet cool moms and actually have fun doing it.',
        mission: 'We plan events so you don\'t have to. Show up, meet people, have a good time. That\'s it. No pressure, no awkwardness, no MLM pitches (we promise).\n\nWe do a mix of kid-friendly stuff (park days, museum trips, coffee mornings) and adults-only events (supper clubs, wine tastings, actual dinners where you can finish a sentence). Because being a mom is amazing, but sometimes you need a break.',
        founderQuote: 'I started this because I was tired of feeling like the only mom who wanted to do more than talk about sleep training. I wanted friends who\'d grab wine on a Tuesday, take the kids to weird museums, or just sit in a park and vent. Turns out a lot of moms wanted the same thing. So here we are!',
        founderName: 'Jessica Wolf',
        founderTitle: 'Founder'
      },
      features: [
        { title: 'Real Connections', description: 'Meet moms in your neighborhood who are in the same stage of life. Just genuine friendships waiting to happen.' },
        { title: 'Fun Events', description: 'Park hangs, coffee dates, yoga, supper clubs, wine nights. We plan it so you can just show up.' },
        { title: 'All Over SF', description: 'Events across the city at the best kid-friendly cafés, parks, restaurants, and sometimes we escape to Napa. Variety is the spice of mom life.' }
      ],
      testimonials: [
        { quote: 'I moved to SF not knowing anyone with kids. Within a month of joining, I had a whole group of mom friends. We text daily now and our kids are obsessed with each other.', name: 'Sarah C.', role: 'Mom of 2 | Pacific Heights' },
        { quote: 'The events are actually fun. Not just standing around at a playground. I look forward to them every month!', name: 'Lauren H.', role: 'Mom of 1 | Marina' },
        { quote: 'Finally found my people! It\'s so nice to be around other moms who want to do more than just talk about sleep schedules. Real friendships, fun outings. 10/10.', name: 'Amanda P.', role: 'Mom of 3 | Noe Valley' }
      ],
      footer: {
        tagline: 'Helping moms in San Francisco meet new friends, bond over good events, and build real community. Come hang with us!'
      }
    };
  });

  const saveContent = () => {
    localStorage.setItem('vivonne_site_content', JSON.stringify(content));
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(null), 3000);
  };

  const generateWithAIForSection = async (section: string, field: string, prompt: string) => {
    setGenerating(`${section}-${field}`);
    const result = await generateWithAI(prompt);
    
    if (section === 'features' || section === 'testimonials') {
      // Handle array fields
      const index = parseInt(field.split('-')[1]);
      const fieldName = field.split('-')[0];
      const newArray = [...content[section]];
      newArray[index] = { ...newArray[index], [fieldName]: result };
      setContent({ ...content, [section]: newArray });
    } else {
      setContent({
        ...content,
        [section]: { ...content[section], [field]: result }
      });
    }
    setGenerating(null);
  };

  const sections = [
    { id: 'hero', label: 'Hero Section', icon: '🏠' },
    { id: 'features', label: 'Features', icon: '✨' },
    { id: 'testimonials', label: 'Testimonials', icon: '💬' },
    { id: 'about', label: 'About Page', icon: '📖' },
    { id: 'footer', label: 'Footer', icon: '📝' },
  ];

  return (
    <div>
      <div className="admin-header">
        <h1>Site Content</h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {saveStatus === 'saved' && (
            <span style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Check size={18} /> Saved!
            </span>
          )}
          <button className="btn btn-primary" onClick={saveContent}>
            Save All Changes
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '24px' }}>
        {/* Section Nav */}
        <div style={{ width: '200px', flexShrink: 0 }}>
          <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: 'none',
                  background: activeSection === section.id ? 'var(--primary-light)' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  fontWeight: activeSection === section.id ? 600 : 400,
                  color: activeSection === section.id ? 'var(--primary-dark)' : 'var(--text-dark)',
                  textAlign: 'left',
                  borderLeft: activeSection === section.id ? '3px solid var(--primary-dark)' : '3px solid transparent'
                }}
              >
                <span>{section.icon}</span>
                {section.label}
              </button>
            ))}
          </div>
          
          <div style={{ marginTop: '16px', padding: '16px', background: 'linear-gradient(135deg, #f0f9ff 0%, #fef3ff 100%)', borderRadius: 'var(--radius-lg)', border: '1px solid #e0e7ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Sparkles size={16} color="var(--primary-dark)" />
              <span style={{ fontWeight: 600, fontSize: '13px' }}>AI Tip</span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
              Click the magic wand button next to any field to generate content with AI!
            </p>
          </div>
        </div>

        {/* Content Editor */}
        <div style={{ flex: 1 }}>
          {/* Hero Section */}
          {activeSection === 'hero' && (
            <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
              <h3 style={{ marginBottom: '20px' }}>Hero Section</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '14px' }}>
                The main banner visitors see when they land on your site.
              </p>
              
              <div className="form-group">
                <label className="form-label">Tagline (small text above headline)</label>
                <input
                  type="text"
                  className="form-input"
                  value={content.hero.tagline}
                  onChange={e => setContent({ ...content, hero: { ...content.hero, tagline: e.target.value } })}
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Main Headline</label>
                  <button
                    className="btn btn-outline btn-small"
                    onClick={() => generateWithAIForSection('hero', 'headline', 'Write a catchy, friendly headline for a San Francisco mom meetup group. Keep it under 8 words. Make it welcoming and fun.')}
                    disabled={generating === 'hero-headline'}
                  >
                    {generating === 'hero-headline' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                    Generate
                  </button>
                </div>
                <input
                  type="text"
                  className="form-input"
                  value={content.hero.headline}
                  onChange={e => setContent({ ...content, hero: { ...content.hero, headline: e.target.value } })}
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Subtext</label>
                  <button
                    className="btn btn-outline btn-small"
                    onClick={() => generateWithAIForSection('hero', 'subtext', 'Write a friendly 2-sentence description for a San Francisco mom meetup group called Vivonne SF. Mention the types of events (coffee meetups, supper clubs, park hangs). Keep it casual and inviting.')}
                    disabled={generating === 'hero-subtext'}
                  >
                    {generating === 'hero-subtext' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                    Generate
                  </button>
                </div>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={content.hero.subtext}
                  onChange={e => setContent({ ...content, hero: { ...content.hero, subtext: e.target.value } })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Primary Button Text</label>
                  <input
                    type="text"
                    className="form-input"
                    value={content.hero.ctaPrimary}
                    onChange={e => setContent({ ...content, hero: { ...content.hero, ctaPrimary: e.target.value } })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Secondary Button Text</label>
                  <input
                    type="text"
                    className="form-input"
                    value={content.hero.ctaSecondary}
                    onChange={e => setContent({ ...content, hero: { ...content.hero, ctaSecondary: e.target.value } })}
                  />
                </div>
              </div>

              {/* Preview */}
              <div style={{ marginTop: '24px', padding: '24px', background: 'var(--gradient)', borderRadius: 'var(--radius-lg)', color: 'white', textAlign: 'center' }}>
                <p style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '2px', opacity: 0.9, marginBottom: '8px' }}>{content.hero.tagline}</p>
                <h2 style={{ fontSize: '28px', marginBottom: '12px' }}>{content.hero.headline}</h2>
                <p style={{ opacity: 0.95, marginBottom: '16px', fontSize: '14px' }}>{content.hero.subtext}</p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <span style={{ padding: '8px 16px', background: 'white', color: 'var(--primary-dark)', borderRadius: '8px', fontSize: '13px' }}>{content.hero.ctaPrimary}</span>
                  <span style={{ padding: '8px 16px', border: '1px solid white', borderRadius: '8px', fontSize: '13px' }}>{content.hero.ctaSecondary}</span>
                </div>
              </div>
            </div>
          )}

          {/* Features Section */}
          {activeSection === 'features' && (
            <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
              <h3 style={{ marginBottom: '20px' }}>Features Section</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '14px' }}>
                The three main value propositions shown on the homepage.
              </p>

              {content.features.map((feature: any, index: number) => (
                <div key={index} style={{ padding: '20px', background: 'var(--cream)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: 600, color: 'var(--primary-dark)' }}>Feature {index + 1}</span>
                    <button
                      className="btn btn-outline btn-small"
                      onClick={() => generateWithAIForSection('features', `description-${index}`, `Write a short, friendly description (2 sentences max) for a mom meetup feature called "${feature.title}". Keep it casual and relatable.`)}
                      disabled={generating === `features-description-${index}`}
                    >
                      {generating === `features-description-${index}` ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                      Generate Description
                    </button>
                  </div>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-input"
                      value={feature.title}
                      onChange={e => {
                        const newFeatures = [...content.features];
                        newFeatures[index] = { ...feature, title: e.target.value };
                        setContent({ ...content, features: newFeatures });
                      }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-textarea"
                      rows={2}
                      value={feature.description}
                      onChange={e => {
                        const newFeatures = [...content.features];
                        newFeatures[index] = { ...feature, description: e.target.value };
                        setContent({ ...content, features: newFeatures });
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Testimonials Section */}
          {activeSection === 'testimonials' && (
            <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
              <h3 style={{ marginBottom: '20px' }}>Testimonials</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '14px' }}>
                Real quotes from your members (you can edit or generate examples).
              </p>

              {content.testimonials.map((testimonial: any, index: number) => (
                <div key={index} style={{ padding: '20px', background: 'var(--cream)', borderRadius: 'var(--radius-md)', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontWeight: 600, color: 'var(--primary-dark)' }}>Testimonial {index + 1}</span>
                    <button
                      className="btn btn-outline btn-small"
                      onClick={() => generateWithAIForSection('testimonials', `quote-${index}`, 'Write a realistic, enthusiastic testimonial from a mom who joined a San Francisco mom meetup group. Make it sound natural and specific (mention supper clubs, making friends, or feeling less isolated). 2-3 sentences.')}
                      disabled={generating === `testimonials-quote-${index}`}
                    >
                      {generating === `testimonials-quote-${index}` ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                      Generate Quote
                    </button>
                  </div>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label className="form-label">Quote</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={testimonial.quote}
                      onChange={e => {
                        const newTestimonials = [...content.testimonials];
                        newTestimonials[index] = { ...testimonial, quote: e.target.value };
                        setContent({ ...content, testimonials: newTestimonials });
                      }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-input"
                        value={testimonial.name}
                        onChange={e => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index] = { ...testimonial, name: e.target.value };
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Details (e.g., "Mom of 2 | Marina")</label>
                      <input
                        type="text"
                        className="form-input"
                        value={testimonial.role}
                        onChange={e => {
                          const newTestimonials = [...content.testimonials];
                          newTestimonials[index] = { ...testimonial, role: e.target.value };
                          setContent({ ...content, testimonials: newTestimonials });
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                className="btn btn-outline"
                onClick={() => setContent({
                  ...content,
                  testimonials: [...content.testimonials, { quote: '', name: '', role: '' }]
                })}
              >
                <Plus size={16} /> Add Testimonial
              </button>
            </div>
          )}

          {/* About Section */}
          {activeSection === 'about' && (
            <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
              <h3 style={{ marginBottom: '20px' }}>About Page</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '14px' }}>
                Your story and mission. This is shown on the About page.
              </p>

              <div className="form-group">
                <label className="form-label">Page Headline</label>
                <input
                  type="text"
                  className="form-input"
                  value={content.about.headline}
                  onChange={e => setContent({ ...content, about: { ...content.about, headline: e.target.value } })}
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Our Story</label>
                  <button
                    className="btn btn-outline btn-small"
                    onClick={() => generateWithAIForSection('about', 'story', 'Write a casual, relatable "our story" section for a San Francisco mom meetup group. Talk about how hard it is to make mom friends as an adult, and how this community started from a few moms grabbing coffee. Keep it friendly and real. 2 paragraphs.')}
                    disabled={generating === 'about-story'}
                  >
                    {generating === 'about-story' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                    Generate
                  </button>
                </div>
                <textarea
                  className="form-textarea"
                  rows={6}
                  value={content.about.story}
                  onChange={e => setContent({ ...content, about: { ...content.about, story: e.target.value } })}
                />
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label" style={{ margin: 0 }}>What We're About</label>
                  <button
                    className="btn btn-outline btn-small"
                    onClick={() => generateWithAIForSection('about', 'mission', 'Write a casual "what we\'re about" section for a mom meetup group. Mention the mix of kid-friendly events and adults-only events like supper clubs and wine nights. Keep it fun and no-pressure. 2 paragraphs.')}
                    disabled={generating === 'about-mission'}
                  >
                    {generating === 'about-mission' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                    Generate
                  </button>
                </div>
                <textarea
                  className="form-textarea"
                  rows={6}
                  value={content.about.mission}
                  onChange={e => setContent({ ...content, about: { ...content.about, mission: e.target.value } })}
                />
              </div>

              <div style={{ padding: '20px', background: 'var(--cream)', borderRadius: 'var(--radius-md)', marginTop: '20px' }}>
                <h4 style={{ marginBottom: '16px' }}>Founder Section</h4>
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label className="form-label" style={{ margin: 0 }}>Founder Quote</label>
                    <button
                      className="btn btn-outline btn-small"
                      onClick={() => generateWithAIForSection('about', 'founderQuote', 'Write a personal, authentic quote from the founder of a mom meetup group explaining why she started it. Make it relatable. Mention wanting friends who would grab wine on a Tuesday or go to interesting museums. Keep it casual and real.')}
                      disabled={generating === 'about-founderQuote'}
                    >
                      {generating === 'about-founderQuote' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                      Generate
                    </button>
                  </div>
                  <textarea
                    className="form-textarea"
                    rows={4}
                    value={content.about.founderQuote}
                    onChange={e => setContent({ ...content, about: { ...content.about, founderQuote: e.target.value } })}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Founder Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={content.about.founderName}
                      onChange={e => setContent({ ...content, about: { ...content.about, founderName: e.target.value } })}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-input"
                      value={content.about.founderTitle}
                      onChange={e => setContent({ ...content, about: { ...content.about, founderTitle: e.target.value } })}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer Section */}
          {activeSection === 'footer' && (
            <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
              <h3 style={{ marginBottom: '20px' }}>Footer</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '14px' }}>
                The tagline shown in the footer of every page.
              </p>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Footer Tagline</label>
                  <button
                    className="btn btn-outline btn-small"
                    onClick={() => generateWithAIForSection('footer', 'tagline', 'Write a short, friendly tagline for a San Francisco mom meetup group footer. Mention community and fun events. Keep it under 25 words.')}
                    disabled={generating === 'footer-tagline'}
                  >
                    {generating === 'footer-tagline' ? <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} />}
                    Generate
                  </button>
                </div>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={content.footer.tagline}
                  onChange={e => setContent({ ...content, footer: { ...content.footer, tagline: e.target.value } })}
                />
              </div>

              {/* Preview */}
              <div style={{ marginTop: '24px', padding: '24px', background: 'var(--secondary)', borderRadius: 'var(--radius-lg)', color: 'white' }}>
                <h4 style={{ marginBottom: '12px' }}>Vivonne SF</h4>
                <p style={{ opacity: 0.7, fontSize: '14px', lineHeight: 1.7 }}>{content.footer.tagline}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Admin Settings
const AdminSettings: React.FC = () => {
  const [emailProvider, setEmailProvider] = useState(() => {
    const saved = localStorage.getItem('vivonne_email_settings');
    return saved ? JSON.parse(saved) : { provider: 'none', apiKey: '', fromEmail: '', fromName: 'Vivonne SF' };
  });
  const [stripeSettings, setStripeSettings] = useState(() => {
    const saved = localStorage.getItem('vivonne_stripe_settings');
    return saved ? JSON.parse(saved) : { publicKey: '', secretKey: '', webhookSecret: '' };
  });
  const [saved, setSaved] = useState(false);
  const [testSending, setTestSending] = useState(false);

  const saveEmailSettings = () => {
    localStorage.setItem('vivonne_email_settings', JSON.stringify(emailProvider));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const saveStripeSettings = () => {
    localStorage.setItem('vivonne_stripe_settings', JSON.stringify(stripeSettings));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const sendTestEmail = async () => {
    setTestSending(true);
    // Simulate sending test email
    await new Promise(resolve => setTimeout(resolve, 2000));
    setTestSending(false);
    alert('Test email sent! Check your inbox.');
  };

  return (
    <div>
      <div className="admin-header">
        <h1>Settings</h1>
        {saved && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--success)' }}>
            <Check size={18} /> Saved!
          </span>
        )}
      </div>

      {/* Email Service Settings */}
      <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Mail size={20} color="var(--primary-dark)" />
          </div>
          <div>
            <h3 style={{ margin: 0 }}>Email Service</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>Connect your email provider to send newsletters</p>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Email Provider</label>
          <select 
            className="form-select"
            value={emailProvider.provider}
            onChange={e => setEmailProvider({ ...emailProvider, provider: e.target.value })}
          >
            <option value="none">Select a provider...</option>
            <option value="mailchimp">Mailchimp</option>
            <option value="mailgun">Mailgun</option>
            <option value="sendgrid">SendGrid</option>
            <option value="postmark">Postmark</option>
            <option value="resend">Resend</option>
          </select>
        </div>

        {emailProvider.provider !== 'none' && (
          <>
            <div className="form-group">
              <label className="form-label">
                {emailProvider.provider === 'mailchimp' ? 'API Key' : 
                 emailProvider.provider === 'mailgun' ? 'API Key' :
                 emailProvider.provider === 'sendgrid' ? 'API Key' :
                 emailProvider.provider === 'postmark' ? 'Server Token' :
                 'API Key'}
              </label>
              <input 
                type="password" 
                className="form-input" 
                value={emailProvider.apiKey}
                onChange={e => setEmailProvider({ ...emailProvider, apiKey: e.target.value })}
                placeholder={`Enter your ${emailProvider.provider} API key`}
              />
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                {emailProvider.provider === 'mailchimp' && 'Find this in Account → Extras → API keys'}
                {emailProvider.provider === 'mailgun' && 'Find this in Settings → API Security'}
                {emailProvider.provider === 'sendgrid' && 'Find this in Settings → API Keys'}
                {emailProvider.provider === 'postmark' && 'Find this in your Server → API Tokens'}
                {emailProvider.provider === 'resend' && 'Find this in your Resend dashboard'}
              </p>
            </div>

            {emailProvider.provider === 'mailgun' && (
              <div className="form-group">
                <label className="form-label">Domain</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={emailProvider.domain || ''}
                  onChange={e => setEmailProvider({ ...emailProvider, domain: e.target.value })}
                  placeholder="mg.yourdomain.com"
                />
              </div>
            )}

            {emailProvider.provider === 'mailchimp' && (
              <div className="form-group">
                <label className="form-label">Audience/List ID</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={emailProvider.audienceId || ''}
                  onChange={e => setEmailProvider({ ...emailProvider, audienceId: e.target.value })}
                  placeholder="Your Mailchimp audience ID"
                />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">From Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  value={emailProvider.fromName}
                  onChange={e => setEmailProvider({ ...emailProvider, fromName: e.target.value })}
                  placeholder="Vivonne SF"
                />
              </div>
              <div className="form-group">
                <label className="form-label">From Email</label>
                <input 
                  type="email" 
                  className="form-input" 
                  value={emailProvider.fromEmail}
                  onChange={e => setEmailProvider({ ...emailProvider, fromEmail: e.target.value })}
                  placeholder="hello@vivonnesf.com"
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
              <button className="btn btn-primary" onClick={saveEmailSettings}>
                Save Email Settings
              </button>
              <button 
                className="btn btn-outline" 
                onClick={sendTestEmail}
                disabled={testSending || !emailProvider.apiKey}
              >
                {testSending ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={16} />}
                Send Test Email
              </button>
            </div>
          </>
        )}

        {emailProvider.provider === 'none' && (
          <div style={{ background: 'var(--cream)', padding: '20px', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
            <Mail size={32} color="var(--text-muted)" style={{ marginBottom: '12px' }} />
            <p style={{ color: 'var(--text-muted)', margin: 0 }}>
              Connect an email service to send newsletters directly from Vivonne
            </p>
          </div>
        )}
      </div>

      {/* Stripe Settings */}
      <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#635bff20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CreditCard size={20} color="#635bff" />
          </div>
          <div>
            <h3 style={{ margin: 0 }}>Stripe Payments</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>Configure Stripe to accept payments for events</p>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Publishable Key</label>
          <input 
            type="text" 
            className="form-input" 
            value={stripeSettings.publicKey}
            onChange={e => setStripeSettings({ ...stripeSettings, publicKey: e.target.value })}
            placeholder="pk_live_..."
          />
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Find this in Stripe Dashboard → Developers → API keys
          </p>
        </div>

        <div className="form-group">
          <label className="form-label">Secret Key</label>
          <input 
            type="password" 
            className="form-input" 
            value={stripeSettings.secretKey}
            onChange={e => setStripeSettings({ ...stripeSettings, secretKey: e.target.value })}
            placeholder="sk_live_..."
          />
        </div>

        <div className="form-group">
          <label className="form-label">Webhook Secret (optional)</label>
          <input 
            type="password" 
            className="form-input" 
            value={stripeSettings.webhookSecret}
            onChange={e => setStripeSettings({ ...stripeSettings, webhookSecret: e.target.value })}
            placeholder="whsec_..."
          />
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Required for automatic payment confirmations
          </p>
        </div>

        <button className="btn btn-primary" onClick={saveStripeSettings}>
          Save Stripe Settings
        </button>
      </div>

      {/* Site Settings */}
      <div style={{ background: 'white', borderRadius: 'var(--radius-lg)', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Settings size={20} color="var(--text-muted)" />
          </div>
          <div>
            <h3 style={{ margin: 0 }}>Site Settings</h3>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>General configuration</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div className="form-group">
            <label className="form-label">Site Name</label>
            <input type="text" className="form-input" defaultValue="Vivonne SF" />
          </div>
          <div className="form-group">
            <label className="form-label">Contact Email</label>
            <input type="email" className="form-input" defaultValue="hello@vivonnesf.com" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Instagram Handle</label>
          <input type="text" className="form-input" placeholder="@vivonnesf" />
        </div>

        <div className="form-group">
          <label className="form-label">Processing Fee ($)</label>
          <input type="number" className="form-input" defaultValue="2.00" step="0.50" style={{ width: '150px' }} />
          <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
            Added to event prices at checkout
          </p>
        </div>

        <button className="btn btn-primary">Save Site Settings</button>
      </div>

      {/* Help Section */}
      <div style={{ marginTop: '24px', padding: '20px', background: 'linear-gradient(135deg, #f0f9ff 0%, #fef3ff 100%)', borderRadius: 'var(--radius-lg)', border: '1px solid #e0e7ff' }}>
        <h4 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={18} color="var(--primary-dark)" /> Need help setting up?
        </h4>
        <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
          Ask the AI assistant in the bottom right corner. It can walk you through connecting Mailchimp, Stripe, or any other integration.
        </p>
      </div>
    </div>
  );
};

// Main App
const App: React.FC = () => {
  return (
    <AuthProvider>
      <DataProvider>
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          <Navbar />
          <main style={{ flex: 1 }}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:id" element={<EventDetailPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </DataProvider>
    </AuthProvider>
  );
};

export default App;
